/* =========================================================================
   Smalley Coffee — Field Notes map controller.
   Leaflet + markercluster, no API key. Reads place data from an inline
   <script type="application/json"> emitted by sections/field-notes.liquid,
   draws monochrome pins (filled = a Favorite / a pick, hollow = on the list),
   and wires category chips + text search + a list synced to the map.

   Depends on Leaflet and Leaflet.markercluster being loaded first (the section
   loads them). Boots itself on DOMContentLoaded; safe if absent.
   ========================================================================= */
(function () {
  'use strict';

  function boot() {
    var root = document.querySelector('[data-sc-fieldnotes]');
    if (!root || typeof L === 'undefined') return;

    var dataEl = root.querySelector('script[type="application/json"]');
    var places = [];
    try { places = JSON.parse(dataEl.textContent) || []; } catch (e) { places = []; }

    // Only mappable rows. Guard against the geocoder having missed a save.
    places = places.filter(function (p) {
      return p && typeof p.lat === 'number' && typeof p.lng === 'number' &&
             !isNaN(p.lat) && !isNaN(p.lng);
    });

    var mapEl  = root.querySelector('[data-sc-fn-map]');
    var listEl = root.querySelector('[data-sc-fn-list]');
    var chipsEl = root.querySelector('[data-sc-fn-chips]');
    var searchEl = root.querySelector('[data-sc-fn-search]');
    var countEl = root.querySelector('[data-sc-fn-count]');
    var emptyEl = root.querySelector('[data-sc-fn-empty]');
    if (!mapEl) return;

    /* ---- state -------------------------------------------------------- */
    var activeCats = {};      // category -> true; empty object = all
    var picksOnly = false;
    var query = '';

    /* ---- map ---------------------------------------------------------- */
    var map = L.map(mapEl, {
      scrollWheelZoom: false,        // don't hijack page scroll
      attributionControl: true,
      zoomControl: true
    });
    // Light, label-quiet monochrome basemap — matches the print system.
    // Esri Light Gray Canvas: free, keyless, no account. Base has no labels;
    // the reference layer adds city/road names on top.
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
      maxZoom: 16
    }).addTo(map);
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 16, pane: 'overlayPane'
    }).addTo(map);
    map.on('click', function () { map.scrollWheelZoom.enable(); }); // opt-in wheel after a click

    function pinIcon(fav) {
      return L.divIcon({
        className: 'sc-fn-pin' + (fav ? ' is-fav' : ''),
        html: '<span class="sc-fn-pin__dot"></span>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        popupAnchor: [0, -8]
      });
    }

    function esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
    function safeUrl(u) {
      try { var x = new URL(String(u)); return (x.protocol === 'http:' || x.protocol === 'https:') ? x.href : ''; }
      catch (e) { return ''; }
    }

    function mapsLink(p) {
      if (p.cid) return 'https://www.google.com/maps?cid=' + encodeURIComponent(p.cid);
      return safeUrl(p.url);   // mock/preview fallback
    }

    function popupHtml(p) {
      var url = mapsLink(p);
      var h = '<div class="sc-fn-pop">';
      h += '<div class="sc-fn-pop__cat">' + esc(p.category || '') + (p.favorite ? ' &middot; a pick' : '') + '</div>';
      h += '<div class="sc-fn-pop__name">' + esc(p.name) + '</div>';
      if (p.addr)  h += '<div class="sc-fn-pop__addr">' + esc(p.addr) + '</div>';
      else if (p.city) h += '<div class="sc-fn-pop__where">' + esc(p.city) + '</div>';
      if (p.note)  h += '<p class="sc-fn-pop__note">' + esc(p.note) + '</p>';
      if (url)     h += '<a class="sc-fn-pop__link" href="' + esc(url) + '" target="_blank" rel="noopener">Open in Maps &rarr;</a>';
      h += '</div>';
      return h;
    }

    // One marker per place, kept alongside its record so filtering is cheap.
    var markers = places.map(function (p) {
      var m = L.marker([p.lat, p.lng], { icon: pinIcon(p.favorite) });
      m.bindPopup(popupHtml(p));
      m._place = p;
      return m;
    });

    var cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 48,
      iconCreateFunction: function (c) {
        return L.divIcon({
          className: 'sc-fn-cluster',
          html: '<span>' + c.getChildCount() + '</span>',
          iconSize: [34, 34]
        });
      }
    });
    map.addLayer(cluster);

    /* ---- categories & chips ------------------------------------------ */
    // Preserve a sensible order; unknown categories append alphabetically.
    var order = ['coffee', 'food', 'drinks', 'stay', 'shopping', 'activity', 'golf'];
    var cats = [];
    places.forEach(function (p) { if (p.category && cats.indexOf(p.category) < 0) cats.push(p.category); });
    cats.sort(function (a, b) {
      var ia = order.indexOf(a), ib = order.indexOf(b);
      if (ia < 0) ia = 99; if (ib < 0) ib = 99;
      return ia - ib || a.localeCompare(b);
    });

    function makeChip(label, key, isPick) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'sc-fn__chip' + (isPick ? ' sc-fn__chip--pick' : '');
      b.textContent = label;
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () {
        if (isPick) { picksOnly = !picksOnly; }
        else if (key === '*') { activeCats = {}; picksOnly = false; }
        else { if (activeCats[key]) delete activeCats[key]; else activeCats[key] = true; }
        syncChips();
        apply(true);
      });
      b._key = key; b._isPick = isPick;
      return b;
    }

    if (chipsEl) {
      chipsEl.appendChild(makeChip('All', '*', false));
      if (places.some(function (p) { return p.favorite; })) {
        chipsEl.appendChild(makeChip('Picks', '__picks', true));
      }
      cats.forEach(function (c) { chipsEl.appendChild(makeChip(c, c, false)); });
    }

    function syncChips() {
      if (!chipsEl) return;
      var noCats = Object.keys(activeCats).length === 0;
      Array.prototype.forEach.call(chipsEl.children, function (b) {
        var on;
        if (b._isPick) on = picksOnly;
        else if (b._key === '*') on = noCats && !picksOnly;
        else on = !!activeCats[b._key];
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    /* ---- filtering ---------------------------------------------------- */
    function matches(p) {
      if (picksOnly && !p.favorite) return false;
      if (Object.keys(activeCats).length && !activeCats[p.category]) return false;
      if (query) {
        var hay = (p.name + ' ' + (p.note || '') + ' ' + (p.city || '') + ' ' + (p.addr || '') + ' ' + (p.category || '')).toLowerCase();
        if (hay.indexOf(query) < 0) return false;
      }
      return true;
    }

    function apply(refit) {
      var shown = [];
      var shownMarkers = [];
      markers.forEach(function (m) {
        if (matches(m._place)) { shown.push(m._place); shownMarkers.push(m); }
      });

      cluster.clearLayers();
      cluster.addLayers(shownMarkers);

      renderList(shown);
      if (countEl) countEl.textContent = shown.length + (shown.length === 1 ? ' place' : ' places');
      if (emptyEl) emptyEl.hidden = shown.length !== 0;

      if (refit && shownMarkers.length) {
        map.fitBounds(cluster.getBounds().pad(0.15), { animate: true, maxZoom: 15 });
      }
    }

    /* ---- list --------------------------------------------------------- */
    function renderList(shown) {
      if (!listEl) return;
      var sorted = shown.slice().sort(function (a, b) {
        if (!!b.favorite - !!a.favorite) return (!!b.favorite) - (!!a.favorite); // picks first
        return String(a.name).localeCompare(String(b.name));
      });
      var frag = document.createDocumentFragment();
      sorted.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'sc-fn__item' + (p.favorite ? ' is-fav' : '');
        var meta = [p.category, p.city].filter(Boolean).join(' &middot; ');
        li.innerHTML =
          '<span class="sc-fn__item-name">' + esc(p.name) + '</span>' +
          '<span class="sc-fn__item-meta">' + meta + '</span>' +
          (p.note ? '<span class="sc-fn__item-note">' + esc(p.note) + '</span>' : '');
        li.addEventListener('click', function () {
          map.setView([p.lat, p.lng], Math.max(map.getZoom(), 14), { animate: true });
          var mk = markers.filter(function (m) { return m._place === p; })[0];
          if (mk) cluster.zoomToShowLayer(mk, function () { mk.openPopup(); });
        });
        frag.appendChild(li);
      });
      listEl.innerHTML = '';
      listEl.appendChild(frag);
    }

    /* ---- search ------------------------------------------------------- */
    if (searchEl) {
      var searchTimer = null;
      searchEl.addEventListener('input', function () {
        query = searchEl.value.trim().toLowerCase();
        apply(false);                          // update list/pins instantly, hold the viewport
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () { apply(true); }, 350); // then ease the map to the results
      });
    }

    /* ---- go ----------------------------------------------------------- */
    if (markers.length) {
      cluster.addLayers(markers);
      map.fitBounds(cluster.getBounds().pad(0.15), { maxZoom: 13 });
    } else {
      map.setView([38.414, -86.952], 12); // fall back to home
    }
    syncChips();
    apply(false);
    setTimeout(function () { map.invalidateSize(); }, 200); // in case it mounted hidden
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
